import { getActivePinia } from 'pinia'
import type { LocationQuery, Router } from 'vue-router'
import { usePilotageViewStore } from '@/stores/pilotageView'
import {
  PILOTAGE_FILTER_PREFIX,
  restoreSectionFilters,
  sectionFilters,
  sectionOfRouteName,
  type PilotageSection,
} from '@/utils/pilotageRoutes'

/**
 * MYAC-1522 3b, drift D11 — moving between the Pilotage sections keeps what
 * each one was filtered on. The last filters of each section are remembered per
 * project in the pilotageView store, written after every navigation that lands
 * on the section (the panel's own filter writes included, so clearing them is
 * remembered too), and put back when the section is entered without filters of
 * its own (`restoreSectionFilters`).
 *
 * Registered by `src/router/index.ts` and by the Pilotage test harness, so both
 * run the same guards. Outside Pinia (a router built alone) it does nothing.
 */
const slotKey = (section: PilotageSection) => `filters.${section}`

function store() {
  return getActivePinia() ? usePilotageViewStore() : null
}

export function installPilotageSectionFilters(router: Router): void {
  router.beforeEach((to, from) =>
    restoreSectionFilters(to, from, (pid, section) => store()?.peek<LocationQuery>(pid, slotKey(section))),
  )
  router.afterEach((to, _from, failure) => {
    if (failure) return
    const section = sectionOfRouteName(to.name)
    if (!section || !PILOTAGE_FILTER_PREFIX[section]) return
    const pilotageView = store()
    if (!pilotageView) return
    pilotageView.slot<LocationQuery>(String(to.params.pid ?? ''), slotKey(section), () => ({})).value =
      sectionFilters(to.query, section)
  })
}
